import {
  CloudUpload as UploadIcon,
  Comment as CommentIcon,
  Contacts as ContactPersonIcon,
  Group as CustomerIcon,
} from "@mui/icons-material";
import { SpeedDial, SpeedDialAction } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import ImportCustomersDialog from "./ImportCustomersDialog";
import type {
  ImportCustomerContactPersonsResponse,
  ImportCustomerNotesResponse,
  ImportCustomersResponse,
} from "../customersApi";

type DialogOpenMode = "CUSTOMERS" | "NOTES" | "CONTACT_PERSON";

interface Props {
  readonly onImportCustomers: (formData: FormData) => Promise<ImportCustomersResponse>;
  readonly onImportNotes: (formData: FormData) => Promise<ImportCustomerNotesResponse>;
  readonly onImportContactPersons: (formData: FormData) => Promise<ImportCustomerContactPersonsResponse>;
}

const CustomerCsvImport: FC<Props> = ({ onImportCustomers, onImportContactPersons, onImportNotes }) => {
  const [open, setOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState<DialogOpenMode | null>(null);
  const handleImportCustomers = async (formData: FormData) => {
    await onImportCustomers(formData);
    setImportDialogOpen(null);
  };

  const handleImportCustomerContactPersons = async (formData: FormData) => {
    await onImportContactPersons(formData);
    setImportDialogOpen(null);
  };

  const handleImportCustomerNotes = async (formData: FormData) => {
    await onImportNotes(formData);
    setImportDialogOpen(null);
  };

  const dialogConfig: Record<
    DialogOpenMode,
    { readonly title: string; readonly handle: (formData: FormData) => Promise<void> }
  > = {
    CUSTOMERS: { title: "Import klientów", handle: handleImportCustomers },
    CONTACT_PERSON: { title: "Import osób kontaktowych", handle: handleImportCustomerContactPersons },
    NOTES: { title: "Import notatek", handle: handleImportCustomerNotes },
  };

  return (
    <div>
      <SpeedDial
        icon={<UploadIcon />}
        ariaLabel="Importuj z CSV"
        direction="up"
        FabProps={{ color: "secondary" }}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        open={open}
      >
        {open && (
          <SpeedDialAction
            icon={<CustomerIcon />}
            slotProps={{ tooltip: { title: "CSV z listą klientów" } }}
            onClick={() => setImportDialogOpen("CUSTOMERS")}
          />
        )}
        {open && (
          <SpeedDialAction
            icon={<ContactPersonIcon />}
            slotProps={{
              tooltip: { title: "CSV z osobami kontaktowymi klientów" },
            }}
            onClick={() => setImportDialogOpen("CONTACT_PERSON")}
          />
        )}
        {open && (
          <SpeedDialAction
            icon={<CommentIcon />}
            slotProps={{ tooltip: { title: "CSV z notatkami klientów" } }}
            onClick={() => setImportDialogOpen("NOTES")}
          />
        )}
      </SpeedDial>
      <ImportCustomersDialog
        title={importDialogOpen ? dialogConfig[importDialogOpen].title : ""}
        open={!!importDialogOpen}
        onClose={() => setImportDialogOpen(null)}
        onNewFile={importDialogOpen ? dialogConfig[importDialogOpen].handle : () => Promise.resolve()}
      />
    </div>
  );
};

export default CustomerCsvImport;
